import React, { useMemo } from 'react';
import { ServiceMark, SmsIcon } from '../sms/SmsMarks';

interface RentalService {
  code: string;
  name: string;
}

interface LongRentalServicePickerProps {
  services: RentalService[];
  selectedCode: string | null;
  query: string;
  voiceOnly: boolean;
  headingId?: string;
  helperText?: string;
  listLabel?: string;
  onQueryChange: (value: string) => void;
  onSelect: (service: RentalService) => void;
}

const POPULAR_SERVICE_NAMES = [
  'Google',
  'Facebook',
  'WhatsApp',
  'Telegram',
  'Instagram',
  'Apple',
  'Microsoft',
  'PayPal',
  'Amazon',
  'TikTok',
];
const MAX_VISIBLE_SERVICES = 60;

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase('ru-RU');
}

const LongRentalServicePicker: React.FC<LongRentalServicePickerProps> = ({
  services,
  selectedCode,
  query,
  voiceOnly,
  headingId = 'long-rent-service-title',
  helperText,
  listLabel = 'Сервисы долгосрочной аренды',
  onQueryChange,
  onSelect,
}) => {
  const popular = useMemo(() => POPULAR_SERVICE_NAMES.flatMap((name) => {
    const service = services.find((item) => normalize(item.name) === normalize(name));
    return service ? [service] : [];
  }), [services]);

  const popularCodes = useMemo(() => new Set(popular.map((service) => service.code)), [popular]);
  const visibleServices = useMemo(() => {
    const normalizedQuery = normalize(query);
    if (normalizedQuery) {
      return services.filter((service) => (
        normalize(service.name).includes(normalizedQuery)
        || normalize(service.code).includes(normalizedQuery)
      ));
    }
    return services.filter((service) => !popularCodes.has(service.code));
  }, [popularCodes, query, services]);

  const selected = services.find((service) => service.code === selectedCode) || null;
  const displayedServices = visibleServices.slice(0, MAX_VISIBLE_SERVICES);

  return (
    <section className="sms-longRent__servicePicker" aria-labelledby={headingId}>
      <div className="sms-longRent__subhead">
        <div>
          <h3 id={headingId}>Выберите сервис</h3>
          <p>{helperText || (voiceOnly ? 'Показаны только сервисы, поддерживающие SMS и входящие звонки.' : 'Популярные сервисы доступны в один клик; остальные найдутся через поиск.')}</p>
        </div>
        <span>{services.length} в каталоге</span>
      </div>

      {popular.length > 0 ? (
        <div className="sms-longRent__popular">
          <strong>Популярные</strong>
          <div>
            {popular.map((service) => (
              <button
                key={service.code}
                type="button"
                className={selectedCode === service.code ? 'is-selected' : ''}
                aria-pressed={selectedCode === service.code}
                onClick={() => onSelect(service)}
              >
                <ServiceMark code={service.code} name={service.name} />
                <span>{service.name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="sms-longRent__serviceSearch">
        <label className="sms-searchField">
          <span className="sr-only">Поиск сервиса</span>
          <SmsIcon name="search" />
          <input
            className="u2-input"
            type="search"
            value={query}
            placeholder="Найти сервис"
            autoComplete="off"
            onChange={(event) => onQueryChange(event.target.value)}
          />
        </label>
        {selected ? (
          <div className="sms-longRent__selectedService" aria-live="polite">
            <ServiceMark code={selected.code} name={selected.name} />
            <span><small>Выбран</small><strong>{selected.name}</strong></span>
          </div>
        ) : null}
      </div>

      <div className="sms-longRent__serviceCatalogHead">
        <strong>{query.trim() ? 'Результаты поиска' : (popular.length ? 'Остальные сервисы' : 'Все сервисы')}</strong>
        <span>{visibleServices.length}</span>
      </div>
      {visibleServices.length > 0 ? (
        <div className="sms-longRent__serviceCatalog" role="listbox" aria-label={listLabel}>
          {displayedServices.map((service) => (
            <button
              key={service.code}
              type="button"
              role="option"
              aria-selected={selectedCode === service.code}
              className={selectedCode === service.code ? 'is-selected' : ''}
              onClick={() => onSelect(service)}
            >
              <ServiceMark code={service.code} name={service.name} />
              <span>{service.name}</span>
              {selectedCode === service.code ? <SmsIcon name="check" /> : null}
            </button>
          ))}
        </div>
      ) : (
        <div className="sms-longRent__serviceEmpty">По запросу «{query.trim()}» ничего не найдено.</div>
      )}
      {visibleServices.length > displayedServices.length ? (
        <p className="sms-longRent__serviceHint" role="status">
          Показаны первые {displayedServices.length} из {visibleServices.length}. Уточните поиск, чтобы найти нужный сервис.
        </p>
      ) : null}
    </section>
  );
};

export default LongRentalServicePicker;
